/**
 * SenritsuX Collaboration & Notification Service
 * Handles collaboration requests and live notifications with Supabase persistence
 * and resilient local-state fallback for preview/offline environments.
 */
import { supabase, isSupabaseConfigured } from './supabase';

const LOCAL_NOTIFICATIONS_KEY = 'senritsux_notifications';
const LOCAL_COLLABS_KEY = 'senritsux_collab_requests';

/** Get local notifications store */
function getLocalNotifications() {
  try {
    const raw = localStorage.getItem(LOCAL_NOTIFICATIONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Save local notifications store */
function saveLocalNotifications(notifications) {
  try {
    localStorage.setItem(LOCAL_NOTIFICATIONS_KEY, JSON.stringify(notifications));
  } catch (err) {
    console.warn('[NotificationService] Local save notice:', err);
  }
}

/** Get local collabs store */
function getLocalCollabs() {
  try {
    const raw = localStorage.getItem(LOCAL_COLLABS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Save local collabs store */
function saveLocalCollabs(collabs) {
  try {
    localStorage.setItem(LOCAL_COLLABS_KEY, JSON.stringify(collabs));
  } catch (err) {
    console.warn('[NotificationService] Local collabs save notice:', err);
  }
}

/** Format Spotify Artist Page URL (must be artist page, not a user ID) */
export function formatSpotifyArtistUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.includes('spotify.com')) return `https://${trimmed}`;
  return `https://open.spotify.com/artist/${trimmed}`;
}

/** Format external profile URL */
export function formatExternalUrl(url, baseDomain = '') {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (baseDomain && !trimmed.includes(baseDomain)) {
    const handle = trimmed.replace(/^@/, '');
    return `https://${baseDomain}/${handle}`;
  }
  return `https://${trimmed}`;
}

/** Format relative time timestamp */
export function formatNotificationTime(isoString) {
  if (!isoString) return 'Just now';
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return 'Recently';
  }
}

/** Get authenticated user ID from active Supabase session or throw 401 Unauthorized */
async function getAuthUserId(fallbackUserId = null) {
  if (isSupabaseConfigured()) {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session?.user?.id) {
        const err = new Error('401 Unauthorized: Authentication required.');
        err.status = 401;
        throw err;
      }
      return session.user.id;
    } catch (err) {
      if (err.status === 401) throw err;
      const authErr = new Error('401 Unauthorized: Authentication required.');
      authErr.status = 401;
      throw authErr;
    }
  }
  return fallbackUserId || 'authenticated_user';
}

/**
 * Send a collaboration request from Artist A to Artist B
 * and generate a notification for Artist B.
 */
export async function sendCollabRequest({
  recipientUserId,
  recipientStageName,
  projectType,
  message,
  senderUser,
  senderProfile,
}) {
  if (!recipientUserId) {
    throw new Error('Recipient artist ID is required.');
  }

  const senderId = await getAuthUserId(senderUser?.id);

  // Prevent self-collaboration requests
  if (senderId === recipientUserId) {
    throw new Error('You cannot send a collaboration request to yourself.');
  }

  // 1. Prevent duplicate pending requests in Supabase
  if (isSupabaseConfigured() && senderId) {
    try {
      const { data: existing, error: checkErr } = await supabase
        .from('collab_requests')
        .select('id, status')
        .eq('sender_user_id', senderId)
        .eq('recipient_user_id', recipientUserId)
        .eq('status', 'pending')
        .limit(1);

      if (!checkErr && existing && existing.length > 0) {
        throw new Error('Collaboration request already sent.');
      }
    } catch (err) {
      if (err.message === 'Collaboration request already sent.') {
        throw err;
      }
      console.warn('[NotificationService] Duplicate check notice:', err.message);
    }
  }

  // Also check local store for duplicates
  const localCollabs = getLocalCollabs();
  const duplicateLocal = localCollabs.find(
    (c) =>
      c.sender_user_id === senderId &&
      c.recipient_user_id === recipientUserId &&
      c.status === 'pending'
  );
  if (duplicateLocal) {
    throw new Error('Collaboration request already sent.');
  }

  // 2. Fetch sender's real profile data from Supabase across profiles and artist_profiles
  let profData = null;
  let artistProfData = null;
  if (isSupabaseConfigured() && senderId) {
    try {
      const [pRes, aRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, name, stage_name, spotify_link, youtube, instagram')
          .eq('id', senderId)
          .maybeSingle(),
        supabase
          .from('artist_profiles')
          .select('id, name, stage_name, spotify_link, youtube, instagram')
          .eq('user_id', senderId)
          .maybeSingle(),
      ]);
      profData = pRes.data || null;
      artistProfData = aRes.data || null;
    } catch (err) {
      console.warn('[NotificationService] Sender profile lookup notice:', err.message);
    }
  }

  // Safe helper to pick the first non-empty string value
  const pickVal = (...vals) => {
    for (const v of vals) {
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
  };

  const senderStageName = pickVal(
    senderProfile?.stage_name,
    profData?.stage_name,
    artistProfData?.stage_name,
    senderUser?.user_metadata?.stage_name,
    ''
  );
  const senderRealName = pickVal(
    senderProfile?.name,
    profData?.name,
    artistProfData?.name,
    senderUser?.user_metadata?.full_name,
    ''
  );
  const senderDisplayName =
    senderStageName ||
    senderRealName ||
    senderUser?.email?.split('@')[0] ||
    'Fellow Creator';

  const rawSpotify = pickVal(
    profData?.spotify_link,
    artistProfData?.spotify_link,
    senderProfile?.spotify_link
  );
  const rawYoutube = pickVal(
    profData?.youtube,
    artistProfData?.youtube,
    senderProfile?.youtube
  );
  const rawInstagram = pickVal(
    profData?.instagram,
    artistProfData?.instagram,
    senderProfile?.instagram
  );

  const spotifyArtistUrl = formatSpotifyArtistUrl(rawSpotify);
  const youtubeUrl = formatExternalUrl(rawYoutube, 'youtube.com');
  const instagramUrl = formatExternalUrl(rawInstagram, 'instagram.com');

  const notificationTitle = 'Collaboration Request';
  const notificationMessage = `New collaboration request from ${senderDisplayName} for ${projectType || 'a project'}.`;
  const timestamp = new Date().toISOString();
  let createdCollabId = `collab_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

  // 3. Persist to Supabase if configured
  if (isSupabaseConfigured() && senderId) {
    try {
      // Insert collab request
      const { data: collabData, error: collabInsertErr } = await supabase
        .from('collab_requests')
        .insert({
          sender_user_id: senderId,
          recipient_user_id: recipientUserId,
          project_type: projectType || 'Feature / Vocal',
          message: message || '',
          status: 'pending',
          created_at: timestamp,
        })
        .select('id')
        .maybeSingle();

      if (collabInsertErr) {
        if (
          collabInsertErr.code === '23505' ||
          collabInsertErr.message?.includes('duplicate key') ||
          collabInsertErr.message?.includes('idx_collab_requests_unique_pending')
        ) {
          throw new Error('Collaboration request already sent.');
        }
        if (
          collabInsertErr.code === '23514' ||
          collabInsertErr.message?.includes('collab_requests_no_self_collab')
        ) {
          throw new Error('You cannot send a collaboration request to yourself.');
        }
        console.error('[NotificationService] Collab insert error:', collabInsertErr);
        throw collabInsertErr;
      }

      if (collabData?.id) {
        createdCollabId = collabData.id;
      }

      // Insert notification for recipient with real social links
      await supabase.from('notifications').insert({
        user_id: recipientUserId,
        title: notificationTitle,
        message: notificationMessage,
        type: 'collab_request',
        data: {
          collab_id: createdCollabId,
          sender_id: senderId,
          sender_name: senderRealName,
          sender_stage_name: senderStageName,
          spotify_link: spotifyArtistUrl,
          youtube: youtubeUrl,
          instagram: instagramUrl,
          project_type: projectType,
          message: message,
          status: 'pending',
        },
        is_read: false,
        created_at: timestamp,
      });
    } catch (err) {
      if (
        err.message === 'Collaboration request already sent.' ||
        err.message === 'You cannot send a collaboration request to yourself.'
      ) {
        throw err;
      }
      console.warn('[NotificationService] Supabase insert notice:', err.message);
    }
  }

  // 4. Save to local storage stores
  const localCollabRecord = {
    id: createdCollabId,
    sender_user_id: senderId,
    recipient_user_id: recipientUserId,
    project_type: projectType || 'Feature / Vocal',
    message: message || '',
    status: 'pending',
    created_at: timestamp,
  };
  localCollabs.unshift(localCollabRecord);
  saveLocalCollabs(localCollabs);

  const localNotifications = getLocalNotifications();
  const newNotification = {
    id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    user_id: recipientUserId,
    title: notificationTitle,
    message: notificationMessage,
    type: 'collab_request',
    data: {
      collab_id: createdCollabId,
      sender_id: senderId,
      sender_name: senderRealName,
      sender_stage_name: senderStageName,
      spotify_link: spotifyArtistUrl,
      youtube: youtubeUrl,
      instagram: instagramUrl,
      project_type: projectType,
      message: message,
      status: 'pending',
    },
    is_read: false,
    created_at: timestamp,
  };

  localNotifications.unshift(newNotification);
  saveLocalNotifications(localNotifications);

  return newNotification;
}

/**
 * Respond to a collaboration request (ACCEPT or DENY/DECLINE).
 * Updates request status, marks recipient's notification as read with status,
 * and sends an automated reciprocal notification to the requester.
 */
export async function respondToCollabRequest({
  collabId,
  notificationId,
  action, // 'accepted' | 'denied' | 'declined'
  senderId,
  recipientUser,
  recipientProfile,
}) {
  const normalizedAction = action === 'accepted' ? 'accepted' : 'denied';
  const recipientId = recipientUser?.id || (await getAuthUserId());
  const recipientDisplayName =
    recipientProfile?.stage_name ||
    recipientProfile?.name ||
    recipientUser?.user_metadata?.stage_name ||
    recipientUser?.user_metadata?.full_name ||
    recipientUser?.email?.split('@')[0] ||
    'Artist';

  const recipientInstagramUrl = formatExternalUrl(recipientProfile?.instagram, 'instagram.com');
  const timestamp = new Date().toISOString();
  let targetRequesterId = senderId;

  // 1. Update in Supabase
  if (isSupabaseConfigured() && collabId) {
    try {
      // Update collab request status
      const { data: updatedCollab } = await supabase
        .from('collab_requests')
        .update({
          status: normalizedAction,
          updated_at: timestamp,
        })
        .eq('id', collabId)
        .select('sender_user_id')
        .maybeSingle();

      if (!targetRequesterId && updatedCollab?.sender_user_id) {
        targetRequesterId = updatedCollab.sender_user_id;
      }

      // Mark the recipient's incoming notification as read and update its status
      if (notificationId && recipientId) {
        await supabase
          .from('notifications')
          .update({
            is_read: true,
          })
          .eq('id', notificationId)
          .eq('user_id', recipientId);
      }

      // Notify the original requester (User A) of the decision
      if (targetRequesterId) {
        const notifTitle =
          normalizedAction === 'accepted'
            ? 'COLLABORATION ACCEPTED'
            : 'COLLABORATION DECLINED';
        const notifMessage =
          normalizedAction === 'accepted'
            ? `Your collaboration request to ${recipientDisplayName} was accepted.`
            : `Your collaboration request to ${recipientDisplayName} was declined.`;

        await supabase.from('notifications').insert({
          user_id: targetRequesterId,
          title: notifTitle,
          message: notifMessage,
          type: 'collab_request',
          data: {
            collab_id: collabId,
            status: normalizedAction,
            action: normalizedAction,
            partner_id: recipientId,
            partner_name: recipientDisplayName,
            partner_instagram: recipientInstagramUrl,
          },
          is_read: false,
          created_at: timestamp,
        });
      }
    } catch (err) {
      console.warn('[NotificationService] respondToCollabRequest Supabase notice:', err.message);
    }
  }

  // 2. Update local storage
  const localCollabs = getLocalCollabs().map((c) =>
    c.id === collabId ? { ...c, status: normalizedAction, updated_at: timestamp } : c
  );
  saveLocalCollabs(localCollabs);

  const localNotifications = getLocalNotifications().map((n) => {
    if (n.id === notificationId) {
      return {
        ...n,
        is_read: true,
        data: { ...(n.data || {}), status: normalizedAction },
      };
    }
    return n;
  });

  // Also push reciprocal notification to local storage
  if (targetRequesterId) {
    const notifTitle =
      normalizedAction === 'accepted'
        ? 'COLLABORATION ACCEPTED'
        : 'COLLABORATION DECLINED';
    const notifMessage =
      normalizedAction === 'accepted'
        ? `Your collaboration request to ${recipientDisplayName} was accepted.`
        : `Your collaboration request to ${recipientDisplayName} was declined.`;

    localNotifications.unshift({
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      user_id: targetRequesterId,
      title: notifTitle,
      message: notifMessage,
      type: 'collab_request',
      data: {
        collab_id: collabId,
        status: normalizedAction,
        action: normalizedAction,
        partner_id: recipientId,
        partner_name: recipientDisplayName,
        partner_instagram: recipientInstagramUrl,
      },
      is_read: false,
      created_at: timestamp,
    });
  }

  saveLocalNotifications(localNotifications);

  return { success: true, status: normalizedAction };
}

/**
 * Fetch all notifications for the given user ID.
 */
export async function fetchUserNotifications(userId) {
  if (!userId) return [];

  let dbNotifications = [];

  if (isSupabaseConfigured()) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (!error && data) {
        dbNotifications = data;
      }
    } catch (err) {
      console.warn('[NotificationService] Fetch notice:', err.message);
    }
  }

  // Fetch local notifications for user
  const localNotifications = getLocalNotifications().filter((n) => n.user_id === userId);

  // Merge uniquely by id or content signature
  const seen = new Set();
  const combined = [];

  [...dbNotifications, ...localNotifications].forEach((item) => {
    const key = item.id || `${item.created_at}_${item.message}`;
    if (!seen.has(key)) {
      seen.add(key);
      combined.push(item);
    }
  });

  return combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationAsRead(notificationId, userId) {
  if (!notificationId) return;

  if (isSupabaseConfigured() && userId) {
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('user_id', userId);
    } catch (err) {
      console.warn('[NotificationService] Mark read notice:', err.message);
    }
  }

  // Update local storage
  const localNotifications = getLocalNotifications().map((n) =>
    n.id === notificationId ? { ...n, is_read: true } : n
  );
  saveLocalNotifications(localNotifications);
}

/**
 * Mark all notifications as read for a user.
 */
export async function markAllNotificationsAsRead(userId) {
  if (!userId) return;

  if (isSupabaseConfigured()) {
    try {
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId);
    } catch (err) {
      console.warn('[NotificationService] Mark all read notice:', err.message);
    }
  }

  // Update local storage
  const localNotifications = getLocalNotifications().map((n) =>
    n.user_id === userId ? { ...n, is_read: true } : n
  );
  saveLocalNotifications(localNotifications);
}
